import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminData');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (period = 'today') => api.get('/dashboard/stats', { params: { period } }),
  getRevenueChart: (days = 30) => api.get(`/dashboard/charts/revenue?days=${days}`),
  getUsersChart: (days = 30) => api.get(`/dashboard/charts/users?days=${days}`),
  getGamesChart: (days = 30) => api.get(`/dashboard/charts/games?days=${days}`),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.patch(`/users/${id}`, data),
  block: (id) => api.post(`/users/${id}/block`),
  unblock: (id) => api.post(`/users/${id}/unblock`),
  freezeWallet: (id) => api.post(`/users/${id}/freeze-wallet`),
  unfreezeWallet: (id) => api.post(`/users/${id}/unfreeze-wallet`),
  changeStatus: (id, status, reason) => api.post(`/users/${id}/change-status?new_status=${status}&reason=${encodeURIComponent(reason || 'Status changed')}`),
  adjustWallet: (data) => api.post(`/users/${data.user_id}/adjust-wallet`, data),
  setResponsibleGaming: (id, settings) => api.post(`/users/${id}/set-responsible-gaming`, settings),
  addNote: (id, note) => api.post(`/users/${id}/add-note?note=${encodeURIComponent(note)}`),
  markRisk: (id, riskScore, fraudCategory, reason) => api.post(`/users/${id}/mark-risk?risk_score=${riskScore}&fraud_category=${fraudCategory}&reason=${encodeURIComponent(reason)}`),
  getTransactions: (id, params) => api.get(`/users/${id}/transactions`, { params }),
  getGameHistory: (id, params) => api.get(`/users/${id}/game-history`, { params }),
  getAdminLogs: (id, params) => api.get(`/users/${id}/admin-logs`, { params }),
  getDevices: (id) => api.get(`/users/${id}/devices`),
  getLoginHistory: (id, params) => api.get(`/users/${id}/login-history`, { params }),
  getReferrals: (id) => api.get(`/users/${id}/referrals`),
  getKyc: (id) => api.get(`/users/${id}/kyc`),
};

// KYC API
export const kycAPI = {
  getAll: (params) => api.get('/kyc', { params }),
  getById: (id) => api.get(`/kyc/${id}`),
  approve: (id) => api.post(`/kyc/${id}/approve`),
  reject: (id, reason) => api.post(`/kyc/${id}/reject?reason=${encodeURIComponent(reason)}`),
  update: (id, data) => api.patch(`/kyc/${id}`, data),
};

// Withdrawals API
export const withdrawalsAPI = {
  getAll: (params) => api.get('/withdrawals', { params }),
  approve: (id) => api.post(`/withdrawals/${id}/approve`),
  reject: (id, reason) => api.post(`/withdrawals/${id}/reject?reason=${encodeURIComponent(reason)}`),
};

// Transactions API
export const transactionsAPI = {
  getAll: (params) => api.get('/transactions', { params }),
};

// Game Management API
export const gamesAPI = {
  getSettings: () => api.get('/game-settings'),
  updateSettings: (variant, data) => api.put(`/game-settings/${variant}`, data),
  getTables: (params) => api.get('/tables', { params }),
  createTable: (data) => api.post('/tables', data),
  getLobby: () => api.get('/lobby'),
  updateLobbyMode: (mode, data) => api.put(`/lobby/modes/${mode}`, data),
  createLobbyTable: (data) => api.post('/lobby/tables', data),
  updateLobbyTable: (id, data) => api.patch(`/lobby/tables/${id}`, data),
  deleteLobbyTable: (id) => api.delete(`/lobby/tables/${id}`),
  getPlatformConfig: () => api.get('/platform-config'),
  updatePlatformConfig: (data) => api.put('/platform-config', data),
  getLiveTables: () => api.get('/live/tables'),
  getLiveTable: (id, reveal = true) => api.get(`/live/tables/${id}?reveal_hands=${reveal}`),
  voidTable: (id, reason) => api.post(`/live/tables/${id}/void`, { reason }),
  getDeclareAudits: (params) => api.get('/declare-audits', { params }),
  getDeclareAuditsForTable: (tableId) => api.get(`/declare-audits/${tableId}`),
};

export const mobileUsersAPI = {
  getAll: (params) => api.get('/mobile-users', { params }),
  dossier: (id) => api.get(`/mobile-users/${id}/dossier`),
  ban: (id, reason) => api.post(`/mobile-users/${id}/ban?reason=${encodeURIComponent(reason || 'Banned')}`),
  unban: (id) => api.post(`/mobile-users/${id}/unban`),
  setKyc: (id, data) => api.post(`/mobile-users/${id}/kyc`, data),
  kycImage: (id, docId, side) => api.get(`/mobile-users/${id}/kyc/${docId}/image`, { params: { side } }),
  adjustWallet: (id, data) => api.post(`/mobile-users/${id}/wallet`, data),
  addNote: (id, note) => api.post(`/mobile-users/${id}/note`, { note }),
};

export const paymentMethodsAPI = {
  getAll: () => api.get('/payment-methods'),
  create: (data) => api.post('/payment-methods', data),
  update: (id, data) => api.patch(`/payment-methods/${id}`, data),
  remove: (id) => api.delete(`/payment-methods/${id}`),
};

export const financeAPI = {
  manualBonus: (data) => api.post('/finance/manual-bonus', data),
  getSummary: (period = 'today') => api.get('/finance/summary', { params: { period } }),
};

export const depositsAPI = {
  getAll: (params) => api.get('/deposits', { params }),
  getById: (id) => api.get(`/deposits/${id}`),
  approve: (id) => api.post(`/deposits/${id}/approve`),
  reject: (id, reason) => api.post(`/deposits/${id}/reject?reason=${encodeURIComponent(reason || 'Rejected')}`),
};

export const gameHistoryAPI = {
  getAll: (params) => api.get('/game-history', { params }),
};

// Tournaments API
export const tournamentsAPI = {
  getAll: (params) => api.get('/tournaments', { params }),
  create: (data) => api.post('/tournaments', data),
  update: (id, data) => api.patch(`/tournaments/${id}`, data),
};

// Bonuses API
export const bonusesAPI = {
  getAll: () => api.get('/bonuses'),
  create: (data) => api.post('/bonuses', data),
  update: (id, data) => api.patch(`/bonuses/${id}`, data),
};

// Promo Codes API
export const promoCodesAPI = {
  getAll: () => api.get('/promo-codes'),
  create: (data) => api.post('/promo-codes', data),
};

// Fraud & Security API
export const fraudAPI = {
  getAlerts: (params) => api.get('/fraud-alerts', { params }),
  resolveAlert: (id, notes) => api.post(`/fraud-alerts/${id}/resolve?notes=${encodeURIComponent(notes || '')}`),
  getAuditLogs: (params) => api.get('/audit-logs', { params }),
};

export const complianceAPI = {
  aml: () => api.get('/compliance/aml'),
};

export const referralsAPI = {
  overview: () => api.get('/referrals/overview'),
};

export const notificationsAPI = {
  summary: () => api.get('/notifications/admin'),
  send: (data) => api.post('/notifications/broadcast', data),
};

// Support API
export const supportAPI = {
  getTickets: (params) => api.get('/support/tickets', { params }),
  updateTicket: (id, data) => api.patch(`/support/tickets/${id}`, data),
};

// Admins API
export const adminsAPI = {
  getAll: () => api.get('/admins'),
  update: (id, data) => api.patch(`/admins/${id}`, data),
};

// Country Settings API
export const countryAPI = {
  getAll: () => api.get('/country-settings'),
  update: (code, data) => api.put(`/country-settings/${code}`, data),
};

// System Settings API
export const systemAPI = {
  get: () => api.get('/system-settings'),
  update: (data) => api.put('/system-settings', data),
};

// Reports API
export const reportsAPI = {
  financial: (params) => api.get('/reports/financial', { params }),
  users: () => api.get('/reports/users'),
  games: () => api.get('/reports/games'),
};

// Seed Data API
export const seedData = () => api.post('/seed-data');

export default api;
